import React, { useState, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { PracticeAreaSchema, BreadcrumbSchema, FAQSchema } from '../components/StructuredData';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Button } from '../components/ui/button';
import {
  ArrowRight, CheckCircle2, Phone, Mail, ChevronDown, Sparkles, Clock, ShieldCheck,
  ClipboardList, Scale, Award, Star, ArrowLeft, FileText, AlertTriangle, Briefcase, Target
} from 'lucide-react';
import { useLocalizedData } from '../i18n/dataLoader';
import Testimonials from '../components/Testimonials';

// Lokale SEO-Metadaten (deutscher Markt) — optimiert auf "Anwalt/Steuerberater + Rechtsgebiet + Bonn"
const SEO_DE = {
  auslaenderrecht: {
    title: 'Anwalt für Ausländerrecht & Einbürgerung in Bonn | Nabhan Gäbler & Partner',
    description: 'Rechtsanwalt für Ausländerrecht in Bonn: Einbürgerung, Aufenthaltstitel, Familiennachzug, Visum. Umfangreiche Erfahrung in Einbürgerungsverfahren. Bundesweit tätig.',
    keywords: 'Anwalt Ausländerrecht Bonn, Rechtsanwalt Einbürgerung, Anwalt Einbürgerung Bonn, Aufenthaltsrecht Bonn, Familiennachzug Anwalt, Anwalt Visum',
  },
  strafrecht: {
    title: 'Strafverteidiger in Bonn – Anwalt für Strafrecht | Nabhan Gäbler & Partner',
    description: 'Strafverteidigung in Bonn & bundesweit: Wirtschaftsstrafrecht, BtMG, Vernehmung, Hauptverhandlung. 24h-Notfall-Hotline. Erstberatung 200 €.',
    keywords: 'Strafverteidiger Bonn, Anwalt Strafrecht Bonn, Rechtsanwalt Strafrecht, Wirtschaftsstrafrecht Anwalt, BtMG Anwalt, Strafverteidigung',
  },
  steuerberatung: {
    title: 'Steuerberater in Bonn – 100 % digitale Steuerberatung | Nabhan Gäbler & Partner',
    description: 'Steuerberater in Bonn: Steuererklärung, Buchhaltung, Jahresabschluss, Lohnabrechnung – 100 % digital, bundesweit. Für Unternehmer & Privatpersonen.',
    keywords: 'Steuerberater Bonn, Steuerberatung Bonn, digitale Steuerberatung, Steuererklärung Bonn, Buchhaltung Steuerberater, Jahresabschluss',
  },
  steuerrecht: {
    title: 'Anwalt für Steuerrecht in Bonn – Betriebsprüfung & Einspruch | Nabhan Gäbler & Partner',
    description: 'Rechtsanwalt für Steuerrecht in Bonn: Betriebsprüfung, Einspruch gegen Steuerbescheid, Finanzgericht, Steuerstrafrecht, Selbstanzeige. Bundesweit tätig.',
    keywords: 'Anwalt Steuerrecht Bonn, Rechtsanwalt Steuerrecht, Betriebsprüfung Anwalt, Einspruch Steuerbescheid, Steuerstrafrecht Anwalt, Selbstanzeige',
  },
  gesellschaftsrecht: {
    title: 'Anwalt für Gesellschaftsrecht in Bonn – GmbH, Verträge, M&A | Nabhan Gäbler & Partner',
    description: 'Rechtsanwalt für Gesellschaftsrecht in Bonn: GmbH-Gründung, Gesellschaftsverträge, Gesellschafterstreit, Unternehmenskauf (M&A). Über 600 betreute Unternehmer.',
    keywords: 'Anwalt Gesellschaftsrecht Bonn, GmbH Gründung Anwalt, Gesellschaftsvertrag, Gesellschafterstreit, M&A Anwalt, Unternehmensrecht Bonn',
  },
  verwaltungsrecht: {
    title: 'Anwalt für Verwaltungsrecht in Bonn – Widerspruch & Klage | Nabhan Gäbler & Partner',
    description: 'Rechtsanwalt für Verwaltungsrecht in Bonn: Widerspruchsverfahren, Klage vor dem Verwaltungsgericht, Behördenstreit, Gewerberecht. Bundesweit tätig.',
    keywords: 'Anwalt Verwaltungsrecht Bonn, Widerspruch Behörde Anwalt, Verwaltungsgericht Klage, Gewerberecht Anwalt, Rechtsanwalt Verwaltungsrecht',
  },
  mietrecht: {
    title: 'Anwalt für Mietrecht in Bonn – Mieter & Vermieter | Nabhan Gäbler & Partner',
    description: 'Rechtsanwalt für Mietrecht in Bonn: Kündigung, Mieterhöhung, Nebenkostenabrechnung, Räumungsklage, Mietminderung. Für Mieter und Vermieter.',
    keywords: 'Anwalt Mietrecht Bonn, Rechtsanwalt Mietrecht, Kündigung Mietvertrag Anwalt, Räumungsklage, Nebenkostenabrechnung Anwalt, Mieterhöhung',
  },
  verkehrsrecht: {
    title: 'Anwalt für Verkehrsrecht in Bonn – Bußgeld & Unfall | Nabhan Gäbler & Partner',
    description: 'Rechtsanwalt für Verkehrsrecht in Bonn: Bußgeldbescheid, Fahrverbot, Punkte in Flensburg, Unfallregulierung, MPU. Bundesweit tätig.',
    keywords: 'Anwalt Verkehrsrecht Bonn, Bußgeldbescheid Anwalt, Fahrverbot Anwalt, Unfall Anwalt Bonn, Verkehrsunfall Rechtsanwalt, MPU',
  },
};

const RechtsgebietPage = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const localized = useLocalizedData('rechtsgebiete');
  const rechtsgebiete = localized?.rechtsgebiete || {};
  const data = rechtsgebiete[slug];
  const [openFaq, setOpenFaq] = useState(0);
  const pageUrl = `https://www.kanzlei-partner.com/rechtsgebiete/${slug}`;
  const breadcrumbItems = useMemo(() => [
    { name: 'Startseite', url: 'https://www.kanzlei-partner.com/' },
    { name: 'Rechtsgebiete', url: 'https://www.kanzlei-partner.com/#rechtsgebiete' },
    { name: data?.rechtsgebietParam, url: pageUrl },
  ], [data?.rechtsgebietParam, pageUrl]);
  const faqItems = useMemo(
    () => (Array.isArray(data?.faqs) ? data.faqs.map((f) => ({ question: f.q, answer: f.a })) : []),
    [data?.faqs],
  );

  if (!data) {
    return <Navigate to="/" replace />;
  }

  const mandantenLink = `/mandantenerfassung?rechtsgebiet=${encodeURIComponent(data.rechtsgebietParam)}`;

  // German market = primary SEO target: use locally optimized titles/descriptions
  const isGerman = (i18n.resolvedLanguage || i18n.language || 'de').startsWith('de');
  const seoMeta = isGerman && SEO_DE[slug] ? SEO_DE[slug] : null;

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={seoMeta?.title || `${data.title || data.eyebrow} – Kanzlei Nabhan Gäbler & Partner`}
        description={seoMeta?.description || data.heroSubtitle || `Professionelle Beratung im ${data.eyebrow}. Bundesweit tätige Kanzlei. Erstberatung 200 €, Abrechnung nach RVG/StBVV.`}
        path={`/rechtsgebiete/${slug}`}
        keywords={seoMeta?.keywords || `${data.eyebrow}, Rechtsanwalt, Kanzlei, ${data.rechtsgebietParam}, Beratung`}
      />
      {faqItems.length > 0 && <FAQSchema items={faqItems} />}
      <PracticeAreaSchema
        name={data.rechtsgebietParam}
        description={data.heroSubtitle || data.intro}
        url={pageUrl}
      />
      <BreadcrumbSchema items={breadcrumbItems} />
      <Header />
      <main className="pt-[150px]">
        {/* HERO */}
        <section className="relative bg-[#1B3A5C] text-white py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 opacity-15 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#b8893d] rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-[#3a6fa0] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <Link to="/" className="inline-flex items-center gap-1 text-white/70 text-sm hover:text-[#b8893d] mb-6 tracking-wide">
              <ArrowLeft className="w-3.5 h-3.5" /> {t('common.back_home', 'Zurück zur Startseite')}
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-5">
                  <span className="w-12 h-px bg-[#b8893d]"></span>
                  {data.eyebrow}
                </div>
                <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
                  {data.heroTitle}
                </h1>
                <p className="text-white/80 text-lg leading-relaxed max-w-xl mb-8">{data.heroSubtitle}</p>

                <div className="flex flex-wrap gap-2 mb-8">
                  {data.badges.map((b) => {
                    const isEmergency = /Notdienst|Notfall/i.test(b);
                    const classes = "inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-sm text-xs tracking-wider border border-white/20";
                    if (isEmergency) {
                      return (
                        <a
                          key={b}
                          href="tel:+4915167775757"
                          data-testid="emergency-hotline-badge"
                          className={`${classes} hover:bg-[#b8893d] hover:border-[#b8893d] transition-colors cursor-pointer`}
                          aria-label="24h-Notfall-Hotline anrufen: +49 151 67775757"
                        >
                          <Phone className="w-3 h-3 text-[#b8893d]" />
                          {b}
                        </a>
                      );
                    }
                    return (
                      <span key={b} className={classes}>
                        <Sparkles className="w-3 h-3 text-[#b8893d]" />
                        {b}
                      </span>
                    );
                  })}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to={mandantenLink}>
                    <Button className="bg-[#b8893d] hover:bg-[#a07730] text-white px-6 sm:px-8 py-6 tracking-[0.1em] sm:tracking-[0.15em] text-xs whitespace-normal h-auto max-w-full">
                      {t('practiceArea.consultation_button', 'JETZT ERSTBERATUNGSTERMIN ANFRAGEN')}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:+4922830406400">
                    <Button variant="outline" className="border-white/40 text-white hover:bg-white hover:text-[#1B3A5C] bg-transparent tracking-wider text-xs px-6 py-6">
                      <Phone className="mr-2 w-3.5 h-3.5" /> +49 (0) 228 30 40 640
                    </Button>
                  </a>
                </div>
              </div>

              {/* Stats Box */}
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-sm p-8">
                <p className="text-[#e0b56a] text-xs tracking-[0.3em] mb-6">{t('practiceArea.balance_eyebrow', 'UNSERE BILANZ')}</p>
                <div className="space-y-6">
                  {data.stats.map((s) => (
                    <div key={s.label} className="flex items-baseline justify-between gap-4 pb-5 border-b border-white/10 last:border-0 last:pb-0">
                      <div className="font-serif text-4xl md:text-5xl text-[#b8893d] leading-none">{s.value}</div>
                      <div className="text-white/80 text-sm text-right max-w-[60%]">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SELBSTTEST (nur Ausländerrecht) */}
        {data.selbsttest && (
          <section className="bg-[#fbf7f0] py-10 border-y border-[#b8893d]/20">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#b8893d] rounded-sm flex items-center justify-center flex-shrink-0">
                    <ClipboardList className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <span className="text-[10px] tracking-wider font-bold bg-[#b8893d] text-white px-1.5 py-0.5 rounded-sm">{t('practiceArea.selbsttest_badge', 'NEU')}</span>
                    <h3 className="font-serif text-2xl text-[#1B3A5C] mt-1">{t('practiceArea.selbsttest_headline', 'Können Sie sich einbürgern lassen?')}</h3>
                    <p className="text-[#34536b] text-sm mt-1">{t('practiceArea.selbsttest_body', 'In 11 Fragen Ihre Chancen prüfen — kostenlos, 3 Minuten.')}</p>
                  </div>
                </div>
                <Link to="/einbuergerungs-selbsttest">
                  <Button className="bg-[#1B3A5C] hover:bg-[#0f2740] text-white tracking-wider text-xs px-8 py-5">
                    {t('practiceArea.selbsttest_cta', 'SELBSTTEST STARTEN')} <ArrowRight className="ml-2 w-3.5 h-3.5" />
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* INTRO */}
        <section className="py-20 bg-white">
          <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <Scale className="w-10 h-10 text-[#b8893d] mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl text-[#1B3A5C] mb-6 leading-tight">
              {data.gender === 'die' ? t('practiceArea.intro_headline_die', 'Spezialisierte Expertise in der') : t('practiceArea.intro_headline_other', 'Spezialisierte Expertise im')} {data.name}
            </h2>
            <p className="text-[#34536b] text-lg leading-relaxed">{data.intro}</p>
          </div>
        </section>

        {/* SERVICES */}
        <section className="py-20 bg-[#f5f8fa]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-14">
              <p className="text-[#b8893d] text-xs tracking-[0.3em] mb-3">{t('practiceArea.services_eyebrow', 'UNSER LEISTUNGSSPEKTRUM')}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1B3A5C]">{t('practiceArea.services_headline', 'Womit wir Ihnen helfen')}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {data.services.map((s) => (
                <div key={s.title} className="group bg-white p-7 rounded-sm border-l-4 border-[#b8893d] hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 bg-[#1B3A5C] group-hover:bg-[#b8893d] rounded-sm flex items-center justify-center transition-colors">
                      <Briefcase className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-serif text-xl text-[#1B3A5C]">{s.title}</h3>
                  </div>
                  <ul className="space-y-2 pl-1">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-[#34536b] text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#b8893d] mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TYPISCHE FÄLLE */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-14">
              <p className="text-[#b8893d] text-xs tracking-[0.3em] mb-3">{t('practiceArea.cases_eyebrow', 'AUS DER PRAXIS')}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1B3A5C] mb-3">{t('practiceArea.cases_headline', 'Typische Fallkonstellationen')}</h2>
              <p className="text-[#34536b] max-w-2xl mx-auto">{t('practiceArea.cases_subline', 'Echte Mandate aus unserer Kanzlei. Anonymisiert, aber 100 % aus dem Alltag.')}</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {data.cases.map((c, idx) => (
                <div key={c.title || idx} className="group relative bg-[#f5f8fa] hover:bg-white p-8 rounded-sm border border-transparent hover:border-[#b8893d]/30 hover:shadow-xl transition-all duration-300">
                  <div className="absolute top-0 right-0 px-3 py-1 bg-[#b8893d] text-white text-[10px] tracking-[0.2em]">
                    {t('practiceArea.case_label', 'FALL')} #{idx + 1}
                  </div>
                  <div className="flex items-start gap-3 mb-4 pt-3">
                    <AlertTriangle className="w-5 h-5 text-[#b8893d] mt-1 flex-shrink-0" />
                    <h3 className="font-serif text-xl text-[#1B3A5C] leading-snug">{c.title}</h3>
                  </div>
                  <p className="text-[#34536b] text-sm leading-relaxed mb-5">{c.story}</p>
                  <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border-l-4 border-emerald-500 rounded-sm">
                    <Award className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-emerald-700 text-sm font-medium">{c.outcome}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* VORGEHENSWEISE */}
        <section className="py-20 bg-[#1B3A5C] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-14">
              <p className="text-[#e0b56a] text-xs tracking-[0.3em] mb-3">{t('practiceArea.process_eyebrow', 'SO ARBEITEN WIR')}</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-3">{t('practiceArea.process_headline', 'Unsere Vorgehensweise')}</h2>
              <p className="text-white/70 max-w-2xl mx-auto">{t('practiceArea.process_subline', 'Klar strukturiert. Transparent. Immer auf Ihren Erfolg ausgerichtet.')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
              {data.process.map((p, idx) => (
                <div key={p.title || idx} className="relative bg-white/5 backdrop-blur-sm border border-white/10 p-6 rounded-sm hover:bg-white/10 transition-colors">
                  <div className="absolute -top-5 left-6 w-10 h-10 bg-[#b8893d] rounded-full flex items-center justify-center font-serif text-lg text-white shadow-lg">
                    {idx + 1}
                  </div>
                  <h3 className="font-serif text-xl mt-3 mb-3">{p.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{p.desc}</p>
                  {idx < data.process.length - 1 && (
                    <ArrowRight className="hidden lg:block absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#b8893d]/50" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TRUST SECTION */}
        <section className="py-20 bg-[#f5f8fa]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: ShieldCheck, title: t('practiceArea.trust_personal_title', 'Persönliche Betreuung'), desc: t('practiceArea.trust_personal_desc', 'Sie haben einen festen Ansprechpartner – kein Call-Center, keine Warteschleife.') },
                { icon: Clock, title: t('practiceArea.trust_response_title', 'Schnelle Reaktion'), desc: t('practiceArea.trust_response_desc', 'Erstantwort innerhalb von 24 Stunden, in Notfällen rund um die Uhr.') },
                { icon: Star, title: t('practiceArea.trust_fees_title', 'Faire Honorare'), desc: t('practiceArea.trust_fees_desc', 'Transparente Kosten – Gegenstandswert oder Streitwert, auf Wunsch Stundensatz.') },
              ].map((tx) => (
                <div key={tx.title} className="bg-white p-7 rounded-sm border-l-4 border-[#b8893d] hover:shadow-md transition-shadow">
                  <tx.icon className="w-7 h-7 text-[#b8893d] mb-4" />
                  <h3 className="text-[#1B3A5C] font-semibold tracking-wide mb-2">{tx.title}</h3>
                  <p className="text-[#34536b] text-sm leading-relaxed">{tx.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS (filtered for this rechtsgebiet) */}
        <Testimonials rechtsgebiet={data.slug} title={`${data.gender === 'die' ? t('practiceArea.testimonials_title_die', 'Erfahrungen aus dem Bereich') : t('practiceArea.testimonials_title_other', 'Erfahrungen aus dem')} ${data.name}`} bg="cream" />

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <p className="text-[#b8893d] text-xs tracking-[0.3em] mb-3">{t('practiceArea.faq_eyebrow', 'HÄUFIGE FRAGEN')}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1B3A5C]">{data.gender === 'die' ? t('practiceArea.faq_headline_die', 'FAQ zur') : t('practiceArea.faq_headline_other', 'FAQ zum')} {data.name}</h2>
            </div>
            <div className="space-y-3">
              {data.faqs.map((f, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div key={f.q || idx} className="bg-[#f5f8fa] rounded-sm border-l-4 border-[#b8893d] overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                      className="w-full flex items-center justify-between gap-3 px-6 py-5 text-left hover:bg-[#eaf1f5] transition-colors"
                    >
                      <span className="text-[#1B3A5C] font-medium text-sm md:text-base">{f.q}</span>
                      <ChevronDown
                        className={`w-4 h-4 text-[#b8893d] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5 text-[#34536b] text-sm leading-relaxed">{f.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 bg-[#1B3A5C] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-15 pointer-events-none">
            <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#b8893d] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <Target className="w-10 h-10 text-[#b8893d] mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-5">
              {t('practiceArea.final_cta_headline', 'Bereit für den nächsten Schritt?')}
            </h2>
            <p className="text-white/80 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              {t('practiceArea.final_cta_body', 'Buchen Sie eine Erstberatung (200 € pauschal, anrechenbar) – vertraulich und unverbindlich. Wir prüfen Ihren Fall und nennen Ihnen die nächsten konkreten Schritte.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={mandantenLink}>
                <Button className="bg-[#b8893d] hover:bg-[#a07730] text-white px-10 py-6 tracking-[0.15em] text-xs">
                  {t('practiceArea.final_cta_button', 'JETZT MANDAT ANFRAGEN')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="mailto:bonn@kanzlei-partner.com">
                <Button variant="outline" className="border-white/40 text-white hover:bg-white hover:text-[#1B3A5C] bg-transparent tracking-wider text-xs px-8 py-6">
                  <Mail className="mr-2 w-3.5 h-3.5" /> bonn@kanzlei-partner.com
                </Button>
              </a>
            </div>

            <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-white/60 text-xs tracking-wider">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#b8893d]" /> {t('practiceArea.consultation_fee', '200 € PAUSCHAL · ANRECHENBAR')}</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#b8893d]" /> {t('practiceArea.billing', 'RVG · STBVV · STUNDENHONORAR')}</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#b8893d]" /> {t('practiceArea.insurance', 'ALLE RECHTSSCHUTZVERSICHERUNGEN')}</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#b8893d]" /> {t('practiceArea.confidential', 'VERTRAULICHE BEHANDLUNG')}</span>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
    </div>
  );
};

export default RechtsgebietPage;
